"use client";
import React, { useEffect, useState } from "react";

import { Switch } from "@nextui-org/react";
import { useTranslations } from "next-intl";

import FormWrapper from "@/ui/Forms/FormWrapper";
import { cn } from "@/utils/cn";

export interface FormSwitchProps {
  name: string;
  label?: string;
  value?: boolean;
  onChange?: (value: boolean) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  description?: string;
  size?: "sm" | "md" | "lg";
  color?: "primary" | "secondary" | "success" | "warning" | "danger";
}

/**
 * FormSwitch component
 * Toggle switch component using NextUI Switch
 */
const FormSwitch: React.FC<FormSwitchProps> = ({
  name,
  label,
  value = false,
  onChange,
  error,
  required = false,
  disabled = false,
  className = "",
  description,
  size = "lg",
  color = "primary",
}) => {
  const t = useTranslations();
  const [isSelected, setIsSelected] = useState<boolean>(value);

  useEffect(() => {
    setIsSelected(value);
  }, [value]);

  const handleChange = () => {
    const newValue = !isSelected;
    setIsSelected(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <FormWrapper
      name={name}
      label={label}
      error={error}
      required={required}
      className={cn("mb-4", className)}
    >
      <div className="flex items-center gap-2">
        <Switch
          name={name}
          isSelected={isSelected}
          onValueChange={handleChange}
          isDisabled={disabled}
          size={size}
          color={color}
          aria-label={label || name}
          classNames={{
            base: "cursor-pointer hover:opacity-90",
            wrapper: "transition-all duration-200 ease-in-out",
          }}
        />
        {description && (
          <span className="text-[0.875rem] text-[#666] ml-2">
            {description}
          </span>
        )}
      </div>
    </FormWrapper>
  );
};

export default FormSwitch;
